(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-acrylic-customizer]";
  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  class ChiclassyAcrylicCustomizer {
    constructor(root) {
      this.root = root;
      this.file = null;
      this.fileUrl = "";
      this.templateTitle = "Uploaded PNG";
      this.shape = "circle";
      this.finish = "clear";
      this.outlineMaskUrl = "";
      this.bleedMaskUrl = "";
      this.outlinePathUrl = "";
      this.outlineReady = false;
      this.outlineImage = null;
      this.outlineArtworkWidth = 100;
      this.outlineTimer = null;
      this.savedCount = 0;
      this.zoom = 1;
      this.x = 0;
      this.y = 0;
      this.drag = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onOpen = this.onOpen.bind(this);
      this.onCount = this.onCount.bind(this);

      root.addEventListener("click", this.onClick);
      root.addEventListener("change", this.onChange);
      root.addEventListener("input", this.onInput);
      root.addEventListener("pointerdown", this.onPointerDown);
      root.addEventListener("pointermove", this.onPointerMove);
      root.addEventListener("pointerup", this.onPointerUp);
      root.addEventListener("pointercancel", this.onPointerUp);
      root.querySelector("form")?.addEventListener("submit", this.onSubmit);
      document.addEventListener("chiclassy:open-acrylic", this.onOpen);
      document.addEventListener("chiclassy:acrylic-count", this.onCount);
      this.initializeVariantButtons();
      this.syncOutlineButton();
      this.updateSlotState();
      this.updateVariantSize();
      this.viewportObserver = typeof IntersectionObserver === "function"
        ? new IntersectionObserver(([entry]) => {
          this.root.dataset.inView = String(entry.isIntersecting && !this.root.hidden);
          document.body.classList.toggle("ca-in-view", entry.isIntersecting && !this.root.hidden);
        }) : null;
      this.viewportObserver?.observe(this.root);
      if (!this.viewportObserver) this.root.dataset.inView = String(!this.root.hidden);
      this.resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(() => {
        if (!this.root.hidden) this.updateVariantSize();
      }) : null;
      const stage = this.get("[data-acrylic-stage]");
      if (stage) this.resizeObserver?.observe(stage);
    }

    get(selector) {
      return this.root.querySelector(selector);
    }

    initializeVariantButtons() {
      const select = this.get("[data-acrylic-variant]");
      if (!select || select.options.length < 2) return;
      let group = this.get("[data-acrylic-variant-buttons]");
      if (!group) {
        group = document.createElement("div");
        group.className = "ca-variant-buttons";
        group.dataset.acrylicVariantButtons = "";
        group.setAttribute("role", "group");
        group.setAttribute("aria-label", "Size / option");
        select.after(group);
      }
      group.replaceChildren();
      [...select.options].forEach((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "ca-variant-button";
        button.dataset.acrylicVariantButton = option.value;
        button.textContent = option.textContent.trim();
        button.disabled = option.disabled;
        group.appendChild(button);
      });
      // Keep the real Shopify variant select for FormData and graceful fallback.
      select.hidden = true;
      const label = this.get(`label[for="${select.id}"]`);
      if (label) { label.id = `${select.id}-label`; group.setAttribute("aria-labelledby", label.id); }
      this.syncVariantButtons();
    }

    syncVariantButtons() {
      const select = this.get("[data-acrylic-variant]");
      this.root.querySelectorAll("[data-acrylic-variant-button]").forEach((button) => {
        const active = button.dataset.acrylicVariantButton === select?.value;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    }

    onClick(event) {
      const variantButton = event.target.closest("[data-acrylic-variant-button]");
      if (variantButton && this.root.contains(variantButton)) {
        if (variantButton.disabled) return;
        const select = this.get("[data-acrylic-variant]");
        const option = [...select.options].find((option) => option.value === variantButton.dataset.acrylicVariantButton);
        if (!option || option.disabled) return;
        select.value = option.value;
        this.clearError();
        this.updatePrice();
        this.updateVariantSize();
        return;
      }
      const template = event.target.closest("[data-template-url]");
      if (template && this.root.contains(template)) {
        this.loadTemplate(template);
        return;
      }
      const shape = event.target.closest("[data-shape]");
      if (shape && this.root.contains(shape)) {
        if (shape.dataset.shape === "custom" && !this.outlineReady) {
          this.showError("Upload a transparent PNG first to use its outline.");
          return;
        }
        this.setChoice("shape", shape.dataset.shape, shape);
        return;
      }
      const finish = event.target.closest("[data-finish]");
      if (finish && this.root.contains(finish)) {
        this.setChoice("finish", finish.dataset.finish, finish);
        return;
      }
      if (event.target.closest("[data-acrylic-reset]")) {
        this.resetPlacement();
        return;
      }
      if (event.target.closest("[data-close-acrylic]")) this.close();
    }

    close() {
      this.root.hidden = true;
      this.root.dataset.inView = "false";
      document.body.classList.remove("ca-in-view");
      document.dispatchEvent(new CustomEvent("chiclassy:acrylic-closed"));
    }

    onChange(event) {
      if (event.target.matches("[data-artwork-input]")) {
        this.loadFile(event.target.files?.[0]);
        return;
      }
      if (event.target.matches("[data-acrylic-variant]")) {
        this.updatePrice();
        this.updateVariantSize();
      }
    }

    onInput(event) {
      if (!event.target.matches("[data-artwork-zoom]")) return;
      this.zoom = Number(event.target.value || 100) / 100;
      this.syncPreview();
    }

    onOpen(event) {
      const requestedProduct = String(event.detail?.productId || "");
      const currentProduct = String(this.root.dataset.productId || "");
      if (requestedProduct && currentProduct && requestedProduct !== currentProduct) return;
      if (this.savedCount >= 2) {
        this.showError("You can add a maximum of 2 custom acrylic pieces.");
        return;
      }
      const variantSelect = this.get("[data-acrylic-variant]");
      if (variantSelect && event.detail?.variantId) {
        const matchingOption = [...variantSelect.options].find((option) => option.value === String(event.detail.variantId));
        if (matchingOption) {
          variantSelect.value = matchingOption.value;
          this.updatePrice();
          this.updateVariantSize();
        }
      }
      this.root.hidden = false;
      document.dispatchEvent(new CustomEvent("chiclassy:acrylic-opened"));
      this.root.dataset.inView = "true";
      this.resetForNext();
      this.setStatus(`Custom acrylic ${this.savedCount + 1} of 2`);
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    onCount(event) {
      this.savedCount = Math.min(2, Number(event.detail?.count || 0));
      this.updateSlotState();
    }

    async loadTemplate(button) {
      const title = button.dataset.templateTitle || "Design template";
      const url = button.dataset.templateUrl;
      if (!url) return;
      this.clearError();
      button.disabled = true;
      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Unable to load this template.");
        const blob = await response.blob();
        const file = new File([blob], `${this.safeFileName(title)}.png`, { type: "image/png" });
        this.templateTitle = title;
        this.setFile(file, title);
        this.root.querySelectorAll("[data-template-url]").forEach((choice) => choice.classList.toggle("is-active", choice === button));
      } catch (error) {
        this.showError(error.message || "Unable to load this template.");
      } finally {
        button.disabled = false;
      }
    }

    loadFile(file) {
      this.clearError();
      if (!file) return;
      if (file.type !== "image/png") {
        this.showError("Please upload a transparent PNG file.");
        this.get("[data-artwork-input]").value = "";
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.showError("The image is larger than 20 MB. Please choose a smaller file.");
        this.get("[data-artwork-input]").value = "";
        return;
      }
      this.templateTitle = "Uploaded PNG";
      this.setFile(file, "Uploaded PNG");
      this.root.querySelectorAll("[data-template-url]").forEach((choice) => choice.classList.remove("is-active"));
    }

    setFile(file, templateTitle) {
      if (this.fileUrl) URL.revokeObjectURL(this.fileUrl);
      this.file = file;
      this.fileUrl = URL.createObjectURL(file);
      this.templateTitle = templateTitle || "Uploaded PNG";
      const image = this.get("[data-artwork-preview]");
      const placeholder = this.get("[data-artwork-placeholder]");
      const title = this.get("[data-upload-title]");
      const detail = this.get("[data-upload-detail]");
      if (image) {
        image.src = this.fileUrl;
        image.hidden = false;
      }
      if (placeholder) placeholder.hidden = true;
      if (title) title.textContent = this.templateTitle;
      if (detail) detail.textContent = `${(file.size / 1024 / 1024).toFixed(1)} MB · Click to replace`;
      const templateProperty = this.get("[data-template-property]");
      if (templateProperty) templateProperty.value = this.templateTitle;
      this.prepareOutline(file);
      this.resetPlacement();
    }

    async prepareOutline(file) {
      this.outlineReady = false;
      this.outlineImage = null;
      this.outlineMaskUrl = "";
      this.bleedMaskUrl = "";
      this.outlinePathUrl = "";
      this.syncOutlineButton();
      if (!(file instanceof File) || file.type !== "image/png") return;

      const sourceUrl = this.fileUrl;
      try {
        const image = new Image();
        image.decoding = "async";
        image.src = sourceUrl;
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = () => reject(new Error("Unable to read the PNG outline."));
        });
        if (this.file !== file || this.fileUrl !== sourceUrl) return;
        const probe = document.createElement("canvas");
        probe.width = 32;
        probe.height = 32;
        const probeContext = probe.getContext("2d", { willReadFrequently: true });
        probeContext.drawImage(image, 0, 0, 32, 32);
        const alpha = probeContext.getImageData(0, 0, 32, 32).data;
        let clearPixels = 0;
        for (let index = 3; index < alpha.length; index += 4) {
          if (alpha[index] < 220) clearPixels += 1;
        }
        if (clearPixels < 10) throw new Error("Outline requires a PNG with a transparent background.");
        this.outlineImage = image;
        this.renderOutline();
        this.outlineReady = true;
        this.syncOutlineButton();
        this.syncCustomOutline();
      } catch (error) {
        this.showError(error.message || "Unable to read the PNG outline.");
      }
    }

    scheduleOutline() {
      if (this.outlineTimer) clearTimeout(this.outlineTimer);
      if (this.shape !== "custom" || !this.outlineImage) return;
      this.outlineTimer = setTimeout(() => {
        this.outlineTimer = null;
        this.renderOutline();
        this.syncCustomOutline();
      }, 70);
    }

    renderOutline() {
      const image = this.outlineImage;
      if (!image) return;
      const size = 256;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("Unable to prepare the PNG outline.");
      const scale = Math.min(204 / image.naturalWidth, 204 / image.naturalHeight);
      const baseWidth = image.naturalWidth * scale;
      const baseHeight = image.naturalHeight * scale;
      this.outlineArtworkWidth = baseWidth / size * 100;
      const width = baseWidth * this.zoom;
      const height = baseHeight * this.zoom;
      const centerX = size * (0.5 + this.x / 100);
      const centerY = size * (0.5 + this.y / 100);
      context.drawImage(image, centerX - width / 2, centerY - height / 2, width, height);
      const pixels = context.getImageData(0, 0, size, size).data;
      const opaque = new Uint8Array(size * size);
      let top = size;
      let topX = 0;
      let topCount = 0;
      let opaqueCount = 0;
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const index = y * size + x;
          if (pixels[index * 4 + 3] <= 48) continue;
          opaque[index] = 1;
          opaqueCount += 1;
          if (y < top) { top = y; topX = x; topCount = 1; }
          else if (y === top) { topX += x; topCount += 1; }
        }
      }
      if (opaqueCount < 20 || opaqueCount > size * size * .86) {
        throw new Error("Use a PNG with a transparent background and a visible design.");
      }

      // A rounded offset gives the artwork a clear border instead of a colored halo.
      const filled = new Uint8Array(size * size);
      const radius = 10;
      const offsets = [];
      for (let dy = -radius; dy <= radius; dy += 1) {
        for (let dx = -radius; dx <= radius; dx += 1) {
          if (dx * dx + dy * dy <= radius * radius) offsets.push([dx, dy]);
        }
      }
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          if (!opaque[y * size + x]) continue;
          for (const [dx, dy] of offsets) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx >= 0 && xx < size && yy >= 0 && yy < size) filled[yy * size + xx] = 1;
          }
        }
      }

      // Join the hanger to the cut shape, then cut the hole from that same shape.
      const holeX = topX / topCount;
      const holeY = Math.max(14, top - 9);
      const outerRadius = 13;
      const holeRadius = 5;
      for (let y = Math.max(0, Math.floor(holeY - outerRadius)); y <= Math.min(size - 1, Math.ceil(holeY + outerRadius)); y += 1) {
        for (let x = Math.max(0, Math.floor(holeX - outerRadius)); x <= Math.min(size - 1, Math.ceil(holeX + outerRadius)); x += 1) {
          if ((x - holeX) ** 2 + (y - holeY) ** 2 <= outerRadius ** 2) filled[y * size + x] = 1;
        }
      }

      const edgeMap = new Map();
      const addEdge = (ax, ay, bx, by) => edgeMap.set(`${ax},${ay}`, [bx, by]);
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          if (!filled[y * size + x]) continue;
          if (y === 0 || !filled[(y - 1) * size + x]) addEdge(x, y, x + 1, y);
          if (x === size - 1 || !filled[y * size + x + 1]) addEdge(x + 1, y, x + 1, y + 1);
          if (y === size - 1 || !filled[(y + 1) * size + x]) addEdge(x + 1, y + 1, x, y + 1);
          if (x === 0 || !filled[y * size + x - 1]) addEdge(x, y + 1, x, y);
        }
      }
      const loops = [];
      while (edgeMap.size) {
        const start = edgeMap.keys().next().value;
        const points = [];
        let key = start;
        while (edgeMap.has(key) && points.length < size * size * 2) {
          const [x, y] = key.split(",").map(Number);
          points.push([x, y]);
          const [nextX, nextY] = edgeMap.get(key);
          edgeMap.delete(key);
          key = `${nextX},${nextY}`;
          if (key === start) break;
        }
        if (points.length > 8) loops.push(points);
      }
      const area = (points) => Math.abs(points.reduce((sum, [x, y], index) => {
        const [nextX, nextY] = points[(index + 1) % points.length];
        return sum + x * nextY - nextX * y;
      }, 0));
      const largest = loops.sort((a, b) => area(b) - area(a))[0];
      if (!largest) throw new Error("Unable to trace the PNG outline.");
      const sampled = largest.filter((_, index) => index % 3 === 0);
      const smoothed = sampled.map(([x, y], index) => {
        const previous = sampled[(index + sampled.length - 1) % sampled.length];
        const next = sampled[(index + 1) % sampled.length];
        return [(previous[0] + x * 2 + next[0]) / 4, (previous[1] + y * 2 + next[1]) / 4];
      });
      const midpoint = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const start = midpoint(smoothed.at(-1), smoothed[0]);
      const commands = [`M ${start[0].toFixed(1)} ${start[1].toFixed(1)}`];
      smoothed.forEach((point, index) => {
        const mid = midpoint(point, smoothed[(index + 1) % smoothed.length]);
        commands.push(`Q ${point[0].toFixed(1)} ${point[1].toFixed(1)} ${mid[0].toFixed(1)} ${mid[1].toFixed(1)}`);
      });
      commands.push("Z");
      const path = commands.join(" ");

      // The same smooth path drives both the white shell and the red cutline.
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><path d="${path}" fill="none" stroke="#e3342f" stroke-width="1.6" stroke-linejoin="round"/><circle cx="${holeX.toFixed(1)}" cy="${holeY.toFixed(1)}" r="${holeRadius}" fill="none" stroke="#e3342f" stroke-width="1.5"/></svg>`;
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = size;
      maskCanvas.height = size;
      const maskContext = maskCanvas.getContext("2d");
      maskContext.fillStyle = "#fff";
      maskContext.fill(new Path2D(path));
      maskContext.globalCompositeOperation = "destination-out";
      maskContext.beginPath();
      maskContext.arc(holeX, holeY, holeRadius, 0, Math.PI * 2);
      maskContext.fill();
      this.outlineMaskUrl = maskCanvas.toDataURL("image/png");
      this.bleedMaskUrl = this.outlineMaskUrl;
      this.outlinePathUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    syncOutlineButton() {
      const button = this.get('[data-shape="custom"]');
      if (!button) return;
      button.disabled = !this.outlineReady;
      button.title = this.outlineReady ? "Die-cut to your PNG silhouette" : "Upload a transparent PNG first";
    }

    syncCustomOutline() {
      const piece = this.get("[data-acrylic-piece]");
      const bleed = this.get("[data-custom-bleed-line]");
      const artwork = this.get("[data-artwork-preview]");
      if (!piece) return;
      if (this.outlineMaskUrl) piece.style.setProperty("--ca-outline-mask", `url(\"${this.outlineMaskUrl}\")`);
      else piece.style.removeProperty("--ca-outline-mask");
      if (this.bleedMaskUrl) piece.style.setProperty("--ca-bleed-mask", `url(\"${this.bleedMaskUrl}\")`);
      else piece.style.removeProperty("--ca-bleed-mask");
      if (bleed) bleed.style.backgroundImage = this.shape === "custom" && this.outlinePathUrl ? `url(\"${this.outlinePathUrl}\")` : "none";
      if (artwork) artwork.style.width = this.shape === "custom" ? `${this.outlineArtworkWidth}%` : "100%";
    }

    setChoice(type, value, activeButton) {
      this.root.querySelectorAll(`[data-${type}]`).forEach((button) => {
        const active = button === activeButton;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
      const piece = this.get("[data-acrylic-piece]");
      const property = this.get(`[data-${type}-property]`);
      if (type === "shape") {
        this.shape = value;
        piece?.classList.remove("is-circle", "is-square", "is-rectangle", "is-heart", "is-custom");
        piece?.classList.add(`is-${value}`);
        this.syncCustomOutline();
        if (value === "custom") this.scheduleOutline();
      } else {
        this.finish = value;
        piece?.classList.remove("is-clear", "is-frosted", "is-glitter");
        piece?.classList.add(`is-${value}`);
      }
      if (property) property.value = type === "shape" && value === "custom" ? "Die-cut" : this.titleCase(value);
    }

    resetPlacement() {
      this.zoom = 1;
      this.x = 0;
      this.y = 0;
      const slider = this.get("[data-artwork-zoom]");
      if (slider) slider.value = "100";
      this.syncPreview();
    }

    resetForNext() {
      this.file = null;
      if (this.fileUrl) URL.revokeObjectURL(this.fileUrl);
      this.fileUrl = "";
      this.templateTitle = "Uploaded PNG";
      const input = this.get("[data-artwork-input]");
      if (input) input.value = "";
      const image = this.get("[data-artwork-preview]");
      const placeholder = this.get("[data-artwork-placeholder]");
      if (image) {
        image.removeAttribute("src");
        image.hidden = true;
      }
      if (placeholder) placeholder.hidden = false;
      const title = this.get("[data-upload-title]");
      const detail = this.get("[data-upload-detail]");
      if (title) title.textContent = "Choose a transparent PNG";
      if (detail) detail.textContent = "Click to upload your design";
      const templateProperty = this.get("[data-template-property]");
      if (templateProperty) templateProperty.value = "Uploaded PNG";
      this.root.querySelectorAll("[data-template-url]").forEach((choice) => choice.classList.remove("is-active"));
      this.outlineReady = false;
      this.outlineImage = null;
      this.outlineMaskUrl = "";
      this.bleedMaskUrl = "";
      this.outlinePathUrl = "";
      if (this.outlineTimer) clearTimeout(this.outlineTimer);
      this.outlineTimer = null;
      const circle = this.get('[data-shape="circle"]');
      if (circle) this.setChoice("shape", "circle", circle);
      this.syncOutlineButton();
      this.syncCustomOutline();
      this.resetPlacement();
      this.clearError();
    }

    onPointerDown(event) {
      const piece = event.target.closest("[data-acrylic-piece]");
      const image = this.get("[data-artwork-preview]");
      if (!piece || !image || image.hidden) return;
      const rect = piece.getBoundingClientRect();
      this.drag = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startX: this.x,
        startY: this.y,
        width: rect.width,
        height: rect.height,
        piece
      };
      piece.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.x = this.clamp(this.drag.startX + ((event.clientX - this.drag.startClientX) / this.drag.width) * 100, -60, 60);
      this.y = this.clamp(this.drag.startY + ((event.clientY - this.drag.startClientY) / this.drag.height) * 100, -60, 60);
      this.syncPreview();
      event.preventDefault();
    }

    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (this.drag.piece.hasPointerCapture?.(event.pointerId)) this.drag.piece.releasePointerCapture(event.pointerId);
      this.drag = null;
      event.preventDefault();
    }

    syncPreview() {
      const image = this.get("[data-artwork-preview]");
      if (image) {
        image.style.left = `${50 + this.x}%`;
        image.style.top = `${50 + this.y}%`;
        image.style.transform = `translate(-50%, -50%) scale(${this.zoom})`;
      }
      const zoom = this.get("[data-zoom-property]");
      const x = this.get("[data-x-property]");
      const y = this.get("[data-y-property]");
      if (zoom) zoom.value = `${Math.round(this.zoom * 100)}%`;
      if (x) x.value = `${this.x.toFixed(1)}%`;
      if (y) y.value = `${this.y.toFixed(1)}%`;
      this.scheduleOutline();
    }

    onSubmit(event) {
      event.preventDefault();
      this.clearError();
      if (this.savedCount >= 2) {
        this.showError("You can add a maximum of 2 custom acrylic pieces.");
        return;
      }
      if (!(this.file instanceof File)) {
        this.showError("Please upload a transparent PNG or choose a design template first.");
        this.get("[data-upload-label]")?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (this.shape === "custom" && !this.outlineReady) {
        this.showError("The PNG outline is still preparing. Please wait a moment and try again.");
        return;
      }
      if (this.shape === "custom" && this.outlineTimer) {
        clearTimeout(this.outlineTimer);
        this.outlineTimer = null;
        this.renderOutline();
        this.syncCustomOutline();
      }
      const variant = this.get("[data-acrylic-variant]")?.selectedOptions?.[0] || this.get("[name='id']");
      if (!variant || variant.disabled || variant.dataset.available === "false") {
        this.showError("This option is sold out. Please choose another size.");
        return;
      }
      const variantId = variant?.value || this.get("[name='id']")?.value;
      const dimensions = this.getActiveVariantDimensions();
      const title = this.get(".ca-order-panel h3")?.textContent.trim() || "Custom acrylic";
      const cartFormData = new FormData(this.root.querySelector("form"));
      cartFormData.set("id", String(variantId || ""));
      cartFormData.set("quantity", "1");
      cartFormData.set("properties[Artwork File]", this.file, this.file.name || "artwork.png");
      const item = {
        customAcrylic: true,
        productId: this.root.dataset.productId || "",
        variantId: String(variantId || ""),
        title,
        variantTitle: variant?.dataset.variantTitle || variant?.textContent.trim().replace(/\s+—\s+Sold out$/, "") || "Default Title",
        price: Number(variant?.dataset.price || 0),
        category: "charm",
        previewImage: this.fileUrl,
        artworkFile: this.file,
        cartFormData,
        shape: this.shape === "custom" ? "Die-cut" : this.titleCase(this.shape),
        finish: this.titleCase(this.finish),
        templateTitle: this.templateTitle,
        x: this.x,
        y: this.y,
        scale: this.zoom,
        rotation: 0,
        layer: 60,
        widthMm: dimensions.width,
        heightMm: dimensions.height,
        outlineMask: this.shape === "custom" ? this.outlineMaskUrl : "",
        bleedMask: this.shape === "custom" ? this.bleedMaskUrl : "",
        outlinePath: this.shape === "custom" ? this.outlinePathUrl : "",
        outlineArtworkWidth: this.shape === "custom" ? this.outlineArtworkWidth : 100
      };
      document.dispatchEvent(new CustomEvent("chiclassy:acrylic-saved", { detail: item }));
      this.setStatus("Saved to your Builder. Add one more acrylic or return to Charms.");
      this.resetForNext();
      if (this.savedCount < 2) this.setStatus(`Saved. Custom acrylic ${this.savedCount + 1} of 2 is ready.`);
      this.close();
    }

    updatePrice() {
      this.syncVariantButtons();
      const option = this.get("[data-acrylic-variant]")?.selectedOptions?.[0] || this.get("[name='id']");
      const priceElement = this.get("[data-acrylic-price]");
      if (priceElement) priceElement.textContent = this.formatMoney(Number(option?.dataset.price || 0));
      this.updateSlotState();
    }

    getActiveVariantDimensions() {
      const select = this.get("[data-acrylic-variant]");
      const option = select?.selectedOptions?.[0] || this.get("[name='id']");
      const explicitWidth = Number(option?.dataset.widthMm || 0);
      const explicitHeight = Number(option?.dataset.heightMm || 0);
      if (explicitWidth > 0 || explicitHeight > 0) {
        return {
          width: explicitWidth || explicitHeight,
          height: explicitHeight || explicitWidth
        };
      }
      const title = option?.dataset.variantTitle || option?.textContent || "";
      const pair = title.match(/(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)\s*mm/i);
      if (pair) return { width: Number(pair[1]), height: Number(pair[2]) };

      const single = title.match(/(\d+(?:\.\d+)?)\s*mm/i);
      const width = single ? Number(single[1]) : 0;
      const height = single ? Number(single[1]) : 0;
      return { width, height };
    }

    updateVariantSize() {
      const piece = this.get("[data-acrylic-piece]");
      const stage = this.get("[data-acrylic-stage]");
      if (!piece || !stage) return;

      const dimensions = this.getActiveVariantDimensions();
      if (!dimensions.width && !dimensions.height) return;

      const widthMm = dimensions.width || dimensions.height;
      const heightMm = dimensions.height || dimensions.width;
      const pixelsPerMm = Math.max(1, Math.min(stage.clientWidth || 520, stage.clientHeight || 470) / 110);
      // Leave room above the body for the hanging tab, including on short screens.
      const maxPixels = Math.min(stage.clientWidth || 520, stage.clientHeight || 470) * 0.74;
      const rawWidth = widthMm * pixelsPerMm;
      const rawHeight = heightMm * pixelsPerMm;
      const fit = Math.min(1, maxPixels / Math.max(rawWidth, rawHeight));

      piece.style.width = `${rawWidth * fit}px`;
      piece.style.height = `${rawHeight * fit}px`;
    }

    updateSlotState() {
      const slot = this.get("[data-acrylic-slot]");
      const option = this.get("[data-acrylic-variant]")?.selectedOptions?.[0] || this.get("[name='id']");
      const unavailable = !option || option.disabled || option.dataset.available === "false";
      if (slot) slot.textContent = this.savedCount >= 2 ? "Maximum 2 custom acrylic pieces" : `Custom acrylic ${this.savedCount + 1} of 2`;
      this.root.querySelectorAll(".ca-submit").forEach((submit) => { submit.disabled = this.savedCount >= 2 || unavailable; });
    }

    setStatus(message) {
      const status = this.get("[data-acrylic-status]");
      if (status) status.textContent = message || "";
    }

    showError(message) {
      const error = this.get("[data-acrylic-error]");
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    clearError() {
      const error = this.get("[data-acrylic-error]");
      if (error) {
        error.textContent = "";
        error.hidden = true;
      }
    }

    safeFileName(value) {
      return String(value || "template").toLowerCase().replace(/[^a-z0-9-_]+/g, "-").replace(/^-+|-+$/g, "") || "template";
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || "USD";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    titleCase(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }
  }

  function init(scope = document) {
    const roots = scope.matches?.(ROOT_SELECTOR) ? [scope] : [...(scope.querySelectorAll?.(ROOT_SELECTOR) || [])];
    roots.forEach((root) => {
      if (!root.__chiclassyAcrylic) root.__chiclassyAcrylic = new ChiclassyAcrylicCustomizer(root);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init(document), { once: true });
  else init(document);
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
